import { API_ROUTES_FOR_AXIOS } from 'src/constants/ApiRoutes';
import { axiosInstance } from 'src/constants/axiosInstance';
import { UNHANDLED_ERROR } from 'src/constants/errors';
import { failureResponse } from 'src/utils/responseUtils';
import { ApiResponse } from 'src/types/commonResponseTypes';
import { handleError } from 'src/utils/commonErrorUtils';
import {
  GenerateUnifiedDigestParams,
  GetUnifiedDigestsQueryParams,
  UnifiedDigestEmailPreferenceUpdatePayload,
  UnifiedDigestEmailPreferenceResponse,
  UnifiedDigestJobStatusResponse,
  UnifiedDigestJobTriggerResponse,
  SuperAdminUnifiedDigestEmailPreferenceListResponse,
  SuperAdminUnifiedDigestEmailPreferenceResponse,
  UnifiedDigestActiveJobResponse,
  UnifiedDigestResponseDto,
  UnifiedDigestSearchParams,
  UnifiedDigestSearchResponse,
  UnifiedDigestTimeframe,
  PersonalActionRecapPreview,
  UnifiedDigestRecapMode,
} from 'src/types/unifiedDigest';

const isValidTimeframe = (
  timeframe?: UnifiedDigestTimeframe,
): timeframe is UnifiedDigestTimeframe => {
  return typeof timeframe === 'string' && timeframe.length > 0;
};

const normalizeApiResponse = <T>(
  payload: ApiResponse<T> | T,
  statusCode?: number,
): ApiResponse<T> => {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    typeof (payload as ApiResponse<T>).success === 'boolean'
  ) {
    const response = payload as ApiResponse<T>;

    if (typeof statusCode === 'number' && response.statusCode !== statusCode) {
      return { ...response, statusCode };
    }

    return response;
  }

  return {
    success: true,
    content: payload as T,
    statusCode,
  };
};

export const getWhatsHappeningDigest = async (
  timeframe?: UnifiedDigestTimeframe,
  referenceDate?: string,
  chatScope?: GenerateUnifiedDigestParams['chatScope'],
  recapMode?: UnifiedDigestRecapMode,
): Promise<
  ApiResponse<UnifiedDigestResponseDto | UnifiedDigestJobTriggerResponse>
> => {
  try {
    const route = API_ROUTES_FOR_AXIOS.getUnifiedDigestWhatsHappening(
      isValidTimeframe(timeframe) ? timeframe : undefined,
      referenceDate,
      chatScope,
      recapMode,
    );
    const response = await axiosInstance.get(route);

    return normalizeApiResponse<
      UnifiedDigestResponseDto | UnifiedDigestJobTriggerResponse
    >(response.data, response.status);
  } catch (error) {
    return handleError(error);
  }
};

export const searchWhatsHappeningDigest = async (
  query: string,
  timeframe?: UnifiedDigestTimeframe,
  limit?: number,
  offset?: number,
  includeRawSources?: boolean,
  chatScope?: UnifiedDigestSearchParams['chatScope'],
  recapMode?: UnifiedDigestRecapMode,
): Promise<ApiResponse<UnifiedDigestSearchResponse>> => {
  try {
    const trimmedQuery = query.trim();

    if (trimmedQuery === '') {
      return failureResponse(UNHANDLED_ERROR);
    }

    const route = API_ROUTES_FOR_AXIOS.searchUnifiedDigestWhatsHappening(
      trimmedQuery,
      isValidTimeframe(timeframe) ? timeframe : undefined,
      limit,
      offset,
      includeRawSources,
      chatScope,
      recapMode,
    );
    const response = await axiosInstance.get(route);

    return normalizeApiResponse<UnifiedDigestSearchResponse>(
      response.data,
      response.status,
    );
  } catch (error) {
    return handleError(error);
  }
};

export const createWhatsHappeningDigestJob = async (
  params: GenerateUnifiedDigestParams,
): Promise<ApiResponse<UnifiedDigestJobTriggerResponse>> => {
  try {
    const route = API_ROUTES_FOR_AXIOS.createUnifiedDigestWhatsHappeningJob();
    const response = await axiosInstance.post(route, params);

    return normalizeApiResponse<UnifiedDigestJobTriggerResponse>(
      response.data,
      response.status,
    );
  } catch (error) {
    return handleError(error);
  }
};

export const getWhatsHappeningDigestJobStatus = async (
  processingId: string,
): Promise<ApiResponse<UnifiedDigestJobStatusResponse>> => {
  try {
    const route =
      API_ROUTES_FOR_AXIOS.getUnifiedDigestWhatsHappeningJobStatus(
        processingId,
      );
    const response = await axiosInstance.get(route);

    return normalizeApiResponse<UnifiedDigestJobStatusResponse>(
      response.data,
      response.status,
    );
  } catch (error) {
    return handleError(error);
  }
};

export const getWhatsHappeningDigestActiveJob = async (
  timeframe?: UnifiedDigestTimeframe,
  chatScope?: GenerateUnifiedDigestParams['chatScope'],
  recapMode?: UnifiedDigestRecapMode,
): Promise<ApiResponse<UnifiedDigestActiveJobResponse | null>> => {
  try {
    const route = API_ROUTES_FOR_AXIOS.getUnifiedDigestWhatsHappeningActiveJob(
      isValidTimeframe(timeframe) ? timeframe : undefined,
      chatScope,
      recapMode,
    );
    const response = await axiosInstance.get(route);

    return normalizeApiResponse<UnifiedDigestActiveJobResponse | null>(
      response.data,
      response.status,
    );
  } catch (error) {
    return handleError(error);
  }
};

export const listUnifiedDigests = async (
  params?: GetUnifiedDigestsQueryParams,
): Promise<ApiResponse<UnifiedDigestResponseDto[]>> => {
  try {
    const route = API_ROUTES_FOR_AXIOS.listUnifiedDigests({
      ...(params?.recapMode ? { recapMode: params.recapMode } : {}),
      timeframe: isValidTimeframe(params?.timeframe)
        ? params?.timeframe
        : undefined,
      chatScope: params?.chatScope,
      limit: params?.limit,
      before: params?.before,
      beforeId: params?.beforeId,
    });
    const { data } = await axiosInstance.get(route);

    return data;
  } catch (error) {
    return handleError(error);
  }
};

export const searchUnifiedDigests = async (
  params: UnifiedDigestSearchParams,
): Promise<ApiResponse<UnifiedDigestSearchResponse>> => {
  try {
    const trimmedQuery = params.query.trim();

    if (trimmedQuery === '') {
      return failureResponse(UNHANDLED_ERROR);
    }

    const route = API_ROUTES_FOR_AXIOS.searchUnifiedDigests({
      ...(params.recapMode ? { recapMode: params.recapMode } : {}),
      query: trimmedQuery,
      timeframe: isValidTimeframe(params.timeframe)
        ? params.timeframe
        : undefined,
      chatScope: params.chatScope,
      limit: params.limit,
      offset: params.offset,
      includeRawSources: params.includeRawSources,
    });
    const response = await axiosInstance.get(route);

    return response.data;
  } catch (error) {
    return handleError(error);
  }
};

export const getUnifiedDigestEmailPreferences = async (): Promise<
  ApiResponse<UnifiedDigestEmailPreferenceResponse>
> => {
  try {
    const route = API_ROUTES_FOR_AXIOS.getUnifiedDigestEmailPreferences();
    const response = await axiosInstance.get(route);

    return normalizeApiResponse<UnifiedDigestEmailPreferenceResponse>(
      response.data,
      response.status,
    );
  } catch (error) {
    return handleError(error);
  }
};

export const updateUnifiedDigestEmailPreferences = async (
  payload: UnifiedDigestEmailPreferenceUpdatePayload,
): Promise<ApiResponse<UnifiedDigestEmailPreferenceResponse>> => {
  try {
    const route = API_ROUTES_FOR_AXIOS.updateUnifiedDigestEmailPreferences();
    const response = await axiosInstance.patch(route, payload);

    return normalizeApiResponse<UnifiedDigestEmailPreferenceResponse>(
      response.data,
      response.status,
    );
  } catch (error) {
    return handleError(error);
  }
};

export const getPersonalActionRecapPreview = async (
  timeframe: 'daily' | 'weekly',
): Promise<ApiResponse<PersonalActionRecapPreview>> => {
  try {
    const route = API_ROUTES_FOR_AXIOS.getPersonalActionRecapPreview(timeframe);
    const response = await axiosInstance.get(route);

    return normalizeApiResponse<PersonalActionRecapPreview>(
      response.data,
      response.status,
    );
  } catch (error) {
    return handleError(error);
  }
};

export const getSuperAdminUnifiedDigestEmailPreferences = async (
  targetUserId: string,
  organizationId?: string,
): Promise<ApiResponse<SuperAdminUnifiedDigestEmailPreferenceResponse>> => {
  try {
    const route =
      API_ROUTES_FOR_AXIOS.getSuperAdminUnifiedDigestEmailPreferencesByOrganization(
        targetUserId,
        organizationId,
      );
    const response = await axiosInstance.get(route);

    return normalizeApiResponse<SuperAdminUnifiedDigestEmailPreferenceResponse>(
      response.data,
      response.status,
    );
  } catch (error) {
    return handleError(error);
  }
};

export const getSuperAdminUnifiedDigestEmailPreferencesForOrganization = async (
  organizationId: string,
  page: number,
  limit: number,
): Promise<ApiResponse<SuperAdminUnifiedDigestEmailPreferenceListResponse>> => {
  try {
    const route =
      API_ROUTES_FOR_AXIOS.getSuperAdminUnifiedDigestEmailPreferencesByOrganizationId(
        organizationId,
        page,
        limit,
      );
    const response = await axiosInstance.get(route);

    return normalizeApiResponse<SuperAdminUnifiedDigestEmailPreferenceListResponse>(
      response.data,
      response.status,
    );
  } catch (error) {
    return handleError(error);
  }
};

export const updateSuperAdminUnifiedDigestEmailPreferences = async (
  targetUserId: string,
  organizationId: string | undefined,
  payload: UnifiedDigestEmailPreferenceUpdatePayload,
): Promise<ApiResponse<SuperAdminUnifiedDigestEmailPreferenceResponse>> => {
  try {
    const route =
      API_ROUTES_FOR_AXIOS.updateSuperAdminUnifiedDigestEmailPreferences(
        targetUserId,
        organizationId,
      );
    const response = await axiosInstance.patch(route, payload);

    return normalizeApiResponse<SuperAdminUnifiedDigestEmailPreferenceResponse>(
      response.data,
      response.status,
    );
  } catch (error) {
    return handleError(error);
  }
};
